import { Box, Field, Spinner } from '@chakra-ui/react';
import { useEffect } from 'react';
import InfoMessage from 'src/components/info-message/InfoMessage';
import TextInput from 'src/components/input/TextInput';
import InputField from 'src/components/input/InputField';
import { useFormContext } from 'react-hook-form';
import TickIcon from 'src/assets/icons/form-elements/TickIcon';
import useCheckPromoCode from '../hooks/useCheckPromoCode';
import { FormValues } from './FormValues';

export default function PromoCode() {
  const {
    register,
    watch,
    formState: { errors, isSubmitted },
    setValue,
    clearErrors,
    setError,
    trigger,
  } = useFormContext<FormValues>();
  const usePromoCode = watch('usePromoCode');
  const promoCode = watch('promoCode');
  const plan = watch('plan');
  const currency = watch('currency');

  const { discount, isLoading } = useCheckPromoCode({ promoCode, plan, currency });

  // Clear promo code when user unchecks the checkbox
  useEffect(() => {
    return () => {
      setValue('promoCode', '');
      setValue('discount', {
        percentOff: 0,
        amountOff: 0,
        terms: null,
        id: null,
        duration: null,
        trialDays: null,
      });
    };
  }, [usePromoCode, setValue]);

  useEffect(() => {
    const validCode = discount && (discount.percentOff > 0 || discount.amountOff > 0);
    setValue('discount', discount);
    // A 100% off promo means the user doesn't have to enter payment details, so check by running
    // validation on these fields again if the form has previously been submitted.
    if (isSubmitted) {
      trigger(['paymentElementComplete', 'postalCode'], { shouldFocus: false });
    }
    if (validCode || !promoCode) {
      clearErrors('promoCode');
    } else {
      setError('promoCode', {
        type: 'manual',
        message: 'Promo code is not valid',
      });
    }
  }, [clearErrors, discount, isLoading, isSubmitted, promoCode, setError, setValue, trigger]);

  const loadingSpinner = isLoading ? <Spinner color="podcastPurple" /> : null;
  const isValid = discount.percentOff > 0 || discount.amountOff > 0;
  const endElement = loadingSpinner ?? (isValid ? <TickIcon color="podcastPurple" /> : null);

  return (
    <Box>
      <InputField
        id="promoCode"
        invalid={!isLoading && !!errors.promoCode}
        errorMessage={isLoading ? undefined : String(errors.promoCode?.message)}
      >
        <Field.Label htmlFor="promoCode">Promo code</Field.Label>
        <TextInput {...register('promoCode')} id="promoCode" inputGroupProps={{ endElement }} />
      </InputField>
      {discount.terms && (
        <Box mt="12px">
          <InfoMessage>{discount.terms}</InfoMessage>
        </Box>
      )}
    </Box>
  );
}
